import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { formatDate, formatDateTime } from '@utils/dateUtils'
import {
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Typography,
  InputAdornment,
  Divider,
  Avatar,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material'
import {
  Add,
  DirectionsCar,
  Speed,
  CloudUpload,
  Delete,
  DirectionsCarFilled,
  ViewColumn,
  ViewColumnOutlined,
  FilterList,
  Clear,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { PageHeader, Button, DataTable, Card, PermissionGate, usePermissions, ExportButtons, AsyncSearchSelect } from '@components/common'
import api from '@services/api'
import { toast } from 'react-toastify'

const STATUS_COLORS = {
  operational: 'success',
  under_maintenance: 'warning',
  out_for_repair: 'info',
  out_of_service: 'error',
  retired: 'default',
}

const VEHICLE_TYPES = [
  { value: 'ambulance', label: 'Ambulance' },
  { value: 'bus', label: 'Bus' },
  { value: 'car', label: 'Car' },
  { value: 'motorcycle', label: 'Motorcycle' },
  { value: 'pickup', label: 'Pickup Truck' },
  { value: 'sedan', label: 'Sedan' },
  { value: 'suv', label: 'SUV' },
  { value: 'truck', label: 'Truck' },
  { value: 'van', label: 'Van' },
  { value: 'other', label: 'Other' },
]

const STATUS_OPTIONS = [
  { value: 'operational', label: 'Operational' },
  { value: 'under_maintenance', label: 'Under Maintenance' },
  { value: 'out_for_repair', label: 'Out for Repair' },
  { value: 'out_of_service', label: 'Out of Service' },
  { value: 'retired', label: 'Retired' },
]

const FUEL_TYPES = [
  { value: 'petrol', label: 'Petrol' },
  { value: 'diesel', label: 'Diesel' },
  { value: 'electric', label: 'Electric' },
  { value: 'hybrid', label: 'Hybrid' },
]

// Essential columns shown by default
const essentialColumns = [
  {
    field: 'image',
    headerName: '',
    width: 60,
    renderCell: (value) => (
      <Avatar
        src={value}
        variant="rounded"
        sx={{ width: 40, height: 40 }}
      >
        <DirectionsCar />
      </Avatar>
    ),
  },
  { field: 'registration_number', headerName: 'Reg #', sortable: true, width: 120 },
  { field: 'name', headerName: 'Name', sortable: true },
  {
    field: 'vehicle_type',
    headerName: 'Type',
    sortable: true,
    renderCell: (value) => value?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
  },
  { field: 'department_name', headerName: 'Section/Unit', sortable: true },
  {
    field: 'status',
    headerName: 'Status',
    sortable: true,
    renderCell: (value, row) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Chip
          label={value?.replace('_', ' ').toUpperCase()}
          color={STATUS_COLORS[value] || 'default'}
          size="small"
        />
        {!row.is_active && (
          <Chip label="INACTIVE" color="error" size="small" variant="outlined" />
        )}
      </Box>
    ),
  },
]

// Additional columns shown when expanded
const additionalColumns = [
  { field: 'vehicle_number', headerName: 'Vehicle #', sortable: true, width: 100 },
  {
    field: 'make',
    headerName: 'Make/Model',
    sortable: true,
    renderCell: (value, row) => `${value || ''} ${row.model || ''}`.trim() || '-',
  },
  { field: 'year', headerName: 'Year', sortable: true, width: 80 },
  { field: 'assigned_driver_name', headerName: 'Driver', sortable: true },
  {
    field: 'current_mileage',
    headerName: 'Mileage',
    sortable: true,
    renderCell: (value) => value ? `${Number(value).toLocaleString()} km` : '-',
  },
]

// Action column (always at the end)
const getActionColumn = (hasPermission, onRequestClick) => hasPermission('vehicle_requests.request') ? [{
  field: 'actions',
  headerName: '',
  width: 120,
  renderCell: (value, row) => (row.status === 'operational' && row.is_active) ? (
    <Button
      size="small"
      variant="outlined"
      startIcon={<DirectionsCarFilled />}
      onClick={(e) => {
        e.stopPropagation()
        onRequestClick(row)
      }}
      sx={{ fontSize: '0.75rem', py: 0.5 }}
    >
      Request
    </Button>
  ) : null,
}] : []

const initialFormData = {
  registration_number: '',
  name: '',
  vehicle_type: 'car',
  make: '',
  model: '',
  year: new Date().getFullYear(),
  color: '',
  vin: '',
  engine_number: '',
  fuel_type: 'diesel',
  status: 'operational',
  is_active: true,
  department: '',
  assigned_driver: '',
  purchase_date: '',
  purchase_cost: '',
  supplier: '',
  insurance_company: '',
  insurance_policy_number: '',
  insurance_expiry: '',
  registration_expiry: '',
  inspection_expiry: '',
  last_service_date: '',
  last_service_mileage: '',
  current_mileage: 0,
  next_service_mileage: '',
  notes: '',
}

const initialRequestFormData = {
  vehicle: '',
  purpose: '',
  destination: '',
  departure_date: '',
  expected_return_date: '',
  num_staff_on_board: 1,
  num_non_staff_on_board: 0,
  requester_rank: '',
}

const VehiclesList = () => {
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const [vehicles, setVehicles] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [detailOpen, setDetailOpen] = useState(false)
  const [selectedVehicle, setSelectedVehicle] = useState(null)
  const [formData, setFormData] = useState(initialFormData)
  const [formLoading, setFormLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [isEditing, setIsEditing] = useState(false)
  const [showAllColumns, setShowAllColumns] = useState(false)
  const [showFilters, setShowFilters] = useState(false)

  // Filter state
  const [filters, setFilters] = useState({
    status: '',
    vehicle_type: '',
    is_active: '',
  })

  // Vehicle Request Modal state
  const [requestModalOpen, setRequestModalOpen] = useState(false)
  const [requestFormData, setRequestFormData] = useState(initialRequestFormData)
  const [requestFormLoading, setRequestFormLoading] = useState(false)
  const [requestErrors, setRequestErrors] = useState({})

  // Image upload state
  const [imageFile, setImageFile] = useState(null)
  const [imageFile2, setImageFile2] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [imagePreview2, setImagePreview2] = useState(null)

  // Options for dropdowns
  const [departments, setDepartments] = useState([])
  const [suppliers, setSuppliers] = useState([])

  // Pagination state
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [totalCount, setTotalCount] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')

  // Refs to track current values for stable callbacks
  const filtersRef = useRef(filters)
  const searchTermRef = useRef(searchTerm)
  const pageSizeRef = useRef(pageSize)

  // Keep refs in sync
  useEffect(() => {
    filtersRef.current = filters
  }, [filters])
  useEffect(() => {
    searchTermRef.current = searchTerm
  }, [searchTerm])
  useEffect(() => {
    pageSizeRef.current = pageSize
  }, [pageSize])

  const fetchVehicles = useCallback(async (currentPage, currentPageSize, search) => {
    try {
      setLoading(true)
      const currentFilters = filtersRef.current
      const params = {
        page: currentPage + 1,  // Backend uses 1-indexed pages
        page_size: currentPageSize,
      }
      if (search) params.search = search
      if (currentFilters.status) params.status = currentFilters.status
      if (currentFilters.vehicle_type) params.vehicle_type = currentFilters.vehicle_type
      if (currentFilters.is_active !== '') params.is_active = currentFilters.is_active
      const response = await api.get('/assets/vehicles/', { params })
      setVehicles(response.data.results || response.data)
      setTotalCount(response.data.count || 0)
    } catch (error) {
      console.error('Error fetching vehicles:', error)
      toast.error('Failed to load vehicles')
    } finally {
      setLoading(false)
    }
  }, [])

  const handlePaginationChange = useCallback(({ page: newPage, pageSize: newPageSize }) => {
    setPage(newPage)
    setPageSize(newPageSize)
    fetchVehicles(newPage, newPageSize, searchTermRef.current)
  }, [fetchVehicles])

  const handleSearch = useCallback((search) => {
    setSearchTerm(search)
    setPage(0)  // Reset to first page on search
    fetchVehicles(0, pageSizeRef.current, search)
  }, [fetchVehicles])

  // Export columns definition
  const exportColumns = useMemo(() => [
    { field: 'registration_number', headerName: 'Reg #' },
    { field: 'name', headerName: 'Name' },
    { field: 'vehicle_type', headerName: 'Type', renderValue: (v) => v?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) },
    { field: 'make', headerName: 'Make' },
    { field: 'model', headerName: 'Model' },
    { field: 'year', headerName: 'Year' },
    { field: 'department_name', headerName: 'Section/Unit' },
    { field: 'assigned_driver_name', headerName: 'Driver' },
    { field: 'current_mileage', headerName: 'Mileage (km)', renderValue: (v) => v ? Number(v).toLocaleString() : '-' },
    { field: 'status', headerName: 'Status', renderValue: (v) => v?.replace('_', ' ').toUpperCase() },
    { field: 'is_active', headerName: 'Active', renderValue: (v) => v ? 'Yes' : 'No' },
  ], [])

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }))
  }

  const clearFilters = () => {
    setFilters({ status: '', vehicle_type: '', is_active: '' })
  }

  const activeFilterCount = Object.values(filters).filter(v => v !== '').length

  const fetchOptions = async () => {
    try {
      const [deptsRes, suppliersRes] = await Promise.all([
        api.get('/common/departments/'),
        api.get('/vendors/'),
      ])
      setDepartments(deptsRes.data.results || deptsRes.data)
      setSuppliers(suppliersRes.data.results || suppliersRes.data)
    } catch (error) {
      console.error('Error fetching options:', error)
    }
  }

  // Initial load and when filters change
  useEffect(() => {
    setPage(0)
    setSearchTerm('')
    fetchVehicles(0, pageSizeRef.current, '')
  }, [filters, fetchVehicles])

  useEffect(() => {
    fetchOptions()
  }, [])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleImageChange = (e, imageNumber) => {
    const file = e.target.files[0]
    if (file) {
      if (imageNumber === 1) {
        setImageFile(file)
        setImagePreview(URL.createObjectURL(file))
      } else {
        setImageFile2(file)
        setImagePreview2(URL.createObjectURL(file))
      }
    }
  }

  const handleRemoveImage = (imageNumber) => {
    if (imageNumber === 1) {
      setImageFile(null)
      setImagePreview(null)
    } else {
      setImageFile2(null)
      setImagePreview2(null)
    }
  }

  const resetImageState = () => {
    setImageFile(null)
    setImageFile2(null)
    setImagePreview(null)
    setImagePreview2(null)
  }

  const handleSubmit = async () => {
    const newErrors = {}
    if (!formData.registration_number) newErrors.registration_number = 'Registration number is required'
    if (!formData.name) newErrors.name = 'Name is required'
    if (!formData.make) newErrors.make = 'Make is required'
    if (!formData.model) newErrors.model = 'Model is required'

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setFormLoading(true)
    try {
      // Use FormData for multipart/form-data (required for image uploads)
      const submitData = new FormData()

      // Add all form fields
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          submitData.append(key, value)
        }
      })

      // Handle nullable fields
      const nullableFields = ['department', 'assigned_driver', 'supplier', 'purchase_cost',
        'last_service_date', 'last_service_mileage', 'next_service_mileage', 'purchase_date',
        'insurance_expiry', 'registration_expiry', 'inspection_expiry']
      nullableFields.forEach(field => {
        if (!formData[field]) {
          submitData.delete(field)
        }
      })

      // Add image files if selected
      if (imageFile) {
        submitData.append('image', imageFile)
      }
      if (imageFile2) {
        submitData.append('image_2', imageFile2)
      }

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }

      if (isEditing && selectedVehicle) {
        await api.patch(`/assets/vehicles/${selectedVehicle.id}/`, submitData, config)
      } else {
        await api.post('/assets/vehicles/', submitData, config)
      }

      fetchVehicles()
      setFormOpen(false)
      setFormData(initialFormData)
      resetImageState()
      setIsEditing(false)
      toast.success(isEditing ? 'Vehicle updated successfully' : 'Vehicle created successfully')
    } catch (error) {
      console.error('Error saving vehicle:', error)
      if (error.response?.data) {
        const backendErrors = {}
        Object.entries(error.response.data).forEach(([key, value]) => {
          backendErrors[key] = Array.isArray(value) ? value[0] : value
        })
        setErrors(backendErrors)
        toast.error('Please fix the errors in the form')
      } else {
        toast.error('Failed to save vehicle')
      }
    } finally {
      setFormLoading(false)
    }
  }

  const handleRowClick = async (row) => {
    // Fetch full vehicle details for the detail view
    try {
      setLoading(true)
      const response = await api.get(`/assets/vehicles/${row.id}/`)
      setSelectedVehicle(response.data)
      setDetailOpen(true)
    } catch (error) {
      console.error('Error fetching vehicle details:', error)
      toast.error('Failed to load vehicle details')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = async () => {
    // Fetch full vehicle details to ensure all fields are populated
    try {
      setFormLoading(true)
      const response = await api.get(`/assets/vehicles/${selectedVehicle.id}/`)
      const vehicle = response.data

      setFormData({
        registration_number: vehicle.registration_number || '',
        name: vehicle.name || '',
        vehicle_type: vehicle.vehicle_type || 'car',
        make: vehicle.make || '',
        model: vehicle.model || '',
        year: vehicle.year || new Date().getFullYear(),
        color: vehicle.color || '',
        vin: vehicle.vin || '',
        engine_number: vehicle.engine_number || '',
        fuel_type: vehicle.fuel_type || 'diesel',
        status: vehicle.status || 'operational',
        is_active: vehicle.is_active !== false,
        department: vehicle.department || '',
        assigned_driver: vehicle.assigned_driver || '',
        purchase_date: vehicle.purchase_date || '',
        purchase_cost: vehicle.purchase_cost || '',
        supplier: vehicle.supplier || '',
        insurance_company: vehicle.insurance_company || '',
        insurance_policy_number: vehicle.insurance_policy_number || '',
        insurance_expiry: vehicle.insurance_expiry || '',
        registration_expiry: vehicle.registration_expiry || '',
        inspection_expiry: vehicle.inspection_expiry || '',
        last_service_date: vehicle.last_service_date || '',
        last_service_mileage: vehicle.last_service_mileage || '',
        current_mileage: vehicle.current_mileage || 0,
        next_service_mileage: vehicle.next_service_mileage || '',
        notes: vehicle.notes || '',
      })
      // Set existing image previews
      setImagePreview(vehicle.image || null)
      setImagePreview2(vehicle.image_2 || null)
      setImageFile(null)
      setImageFile2(null)
      setIsEditing(true)
      setDetailOpen(false)
      setFormOpen(true)
    } catch (error) {
      console.error('Error fetching vehicle details:', error)
      toast.error('Failed to load vehicle details for editing')
    } finally {
      setFormLoading(false)
    }
  }

  // Vehicle Request handlers
  const handleOpenRequestModal = (vehicle) => {
    setSelectedVehicle(vehicle)
    setRequestFormData({
      ...initialRequestFormData,
      vehicle: vehicle.id,
    })
    setRequestErrors({})
    setRequestModalOpen(true)
  }

  // Memoize columns based on toggle state
  const columns = useMemo(() => {
    const actionCols = getActionColumn(hasPermission, handleOpenRequestModal)
    if (showAllColumns) {
      return [
        ...essentialColumns.slice(0, 3), // Image, Reg #, Name
        ...additionalColumns.slice(0, 3), // Vehicle #, Make/Model, Year
        essentialColumns[3], // Type
        essentialColumns[4], // Section/Unit
        additionalColumns[3], // Driver
        additionalColumns[4], // Mileage
        essentialColumns[5], // Status
        ...actionCols,
      ]
    }
    return [...essentialColumns, ...actionCols]
  }, [showAllColumns, hasPermission])

  const handleRequestChange = (e) => {
    const { name, value } = e.target
    setRequestFormData((prev) => ({ ...prev, [name]: value }))
    if (requestErrors[name]) {
      setRequestErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleRequestSubmit = async () => {
    const newErrors = {}
    if (!requestFormData.purpose) newErrors.purpose = 'Purpose is required'
    if (!requestFormData.destination) newErrors.destination = 'Destination is required'
    if (!requestFormData.departure_date) newErrors.departure_date = 'Departure date is required'
    if (!requestFormData.expected_return_date) newErrors.expected_return_date = 'Return date is required'

    if (Object.keys(newErrors).length > 0) {
      setRequestErrors(newErrors)
      return
    }

    setRequestFormLoading(true)
    try {
      await api.post('/assets/vehicle-requests/', requestFormData)
      toast.success('Vehicle request submitted successfully')
      setRequestModalOpen(false)
      setRequestFormData(initialRequestFormData)
      setDetailOpen(false)
    } catch (error) {
      console.error('Error creating request:', error)
      const errorData = error.response?.data
      if (errorData) {
        // Handle different error formats from backend
        if (typeof errorData === 'string') {
          toast.error(errorData)
        } else if (errorData.error) {
          toast.error(errorData.error)
        } else if (errorData.detail) {
          toast.error(errorData.detail)
        } else if (errorData.non_field_errors) {
          toast.error(Array.isArray(errorData.non_field_errors) ? errorData.non_field_errors[0] : errorData.non_field_errors)
        } else {
          // Field-specific errors
          const backendErrors = {}
          Object.entries(errorData).forEach(([key, value]) => {
            backendErrors[key] = Array.isArray(value) ? value[0] : value
          })
          setRequestErrors(backendErrors)
          // Show first error in toast
          const firstError = Object.values(backendErrors)[0]
          if (firstError) {
            toast.error(firstError)
          }
        }
      } else {
        toast.error('Failed to submit vehicle request')
      }
    } finally {
      setRequestFormLoading(false)
    }
  }

  const isExpiringOrExpired = (date) => {
    if (!date) return false
    const expiryDate = new Date(date)
    const today = new Date()
    const thirtyDaysFromNow = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000)
    return expiryDate <= thirtyDaysFromNow
  }

  return (
    <Box>
      <PageHeader
        title="Vehicles"
        subtitle="Manage organizational vehicles"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Assets', path: '/assets' },
          { label: 'Vehicles' },
        ]}
        actions={
          <Stack direction="row" spacing={1}>
            <ExportButtons
              data={vehicles}
              columns={exportColumns}
              title="Vehicles List"
              filename="vehicles"
              filters={{
                Status: filters.status ? STATUS_OPTIONS.find(o => o.value === filters.status)?.label : null,
                Type: filters.vehicle_type ? VEHICLE_TYPES.find(o => o.value === filters.vehicle_type)?.label : null,
                Active: filters.is_active !== '' ? (filters.is_active === 'true' ? 'Yes' : 'No') : null,
              }}
            />
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => setShowFilters(!showFilters)}
              color={activeFilterCount > 0 ? 'primary' : 'inherit'}
            >
              Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
            </Button>
            <Tooltip title={showAllColumns ? 'Show fewer columns' : 'Show all columns'}>
              <Button
                variant="outlined"
                startIcon={showAllColumns ? <ViewColumn /> : <ViewColumnOutlined />}
                onClick={() => setShowAllColumns(!showAllColumns)}
              >
                {showAllColumns ? 'Less' : 'More'}
              </Button>
            </Tooltip>
            <PermissionGate permission="vehicles.create">
              <Button startIcon={<Add />} onClick={() => { setIsEditing(false); setFormData(initialFormData); resetImageState(); setFormOpen(true) }}>
                Add Vehicle
              </Button>
            </PermissionGate>
          </Stack>
        }
      />

      {/* Filters Panel */}
      {showFilters && (
        <Card sx={{ mb: 2, p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">All</MenuItem>
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  value={filters.vehicle_type}
                  onChange={(e) => handleFilterChange('vehicle_type', e.target.value)}
                  label="Type"
                >
                  <MenuItem value="">All</MenuItem>
                  {VEHICLE_TYPES.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Active</InputLabel>
                <Select
                  value={filters.is_active}
                  onChange={(e) => handleFilterChange('is_active', e.target.value)}
                  label="Active"
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                variant="text"
                startIcon={<Clear />}
                onClick={clearFilters}
                disabled={activeFilterCount === 0}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Card>
      )}

      <Card>
        <DataTable
          columns={columns}
          data={vehicles}
          loading={loading}
          onRowClick={handleRowClick}
          searchPlaceholder="Search vehicles..."
          emptyMessage="No vehicles found"
          serverSide
          totalCount={totalCount}
          page={page}
          rowsPerPage={pageSize}
          onPaginationChange={handlePaginationChange}
          onSearch={handleSearch}
        />
      </Card>

      {/* Create/Edit Vehicle Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{isEditing ? 'Edit Vehicle' : 'Add New Vehicle'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {/* Basic Info */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                Basic Information
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Registration Number"
                name="registration_number"
                value={formData.registration_number}
                onChange={handleChange}
                error={Boolean(errors.registration_number)}
                helperText={errors.registration_number}
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Vehicle Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={Boolean(errors.name)}
                helperText={errors.name}
                required
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Vehicle Type</InputLabel>
                <Select
                  name="vehicle_type"
                  value={formData.vehicle_type}
                  onChange={handleChange}
                  label="Vehicle Type"
                >
                  {VEHICLE_TYPES.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Make"
                name="make"
                value={formData.make}
                onChange={handleChange}
                error={Boolean(errors.make)}
                helperText={errors.make}
                required
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Model"
                name="model"
                value={formData.model}
                onChange={handleChange}
                error={Boolean(errors.model)}
                helperText={errors.model}
                required
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Year"
                name="year"
                type="number"
                value={formData.year}
                onChange={handleChange}
              />
            </Grid>

            {/* Assignment */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Assignment
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  label="Status"
                >
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">Active</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Inactive vehicles cannot be booked
                    </Typography>
                  </Box>
                }
                sx={{ mt: 1 }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Section/Unit</InputLabel>
                <Select
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  label="Section/Unit"
                >
                  <MenuItem value="">None</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <AsyncSearchSelect
                endpoint="/common/users/"
                value={formData.assigned_driver}
                onChange={(id) => setFormData((prev) => ({ ...prev, assigned_driver: id }))}
                getOptionLabel={(user) => {
                  const name = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || 'Unknown'
                  return user.email ? `${name} (${user.email})` : name
                }}
                label="Assigned Driver"
              />
            </Grid>

            {/* Additional Vehicle Details */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Additional Details
                  </Typography>
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Color"
                    name="color"
                    value={formData.color}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="VIN"
                    name="vin"
                    value={formData.vin}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Engine Number"
                    name="engine_number"
                    value={formData.engine_number}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <FormControl fullWidth>
                    <InputLabel>Fuel Type</InputLabel>
                    <Select
                      name="fuel_type"
                      value={formData.fuel_type}
                      onChange={handleChange}
                      label="Fuel Type"
                    >
                      {FUEL_TYPES.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Insurance & Registration */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Insurance & Registration
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Insurance Company"
                    name="insurance_company"
                    value={formData.insurance_company}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Insurance Policy #"
                    name="insurance_policy_number"
                    value={formData.insurance_policy_number}
                    onChange={handleChange}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Insurance Expiry"
                    name="insurance_expiry"
                    type="date"
                    value={formData.insurance_expiry}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Registration Expiry"
                    name="registration_expiry"
                    type="date"
                    value={formData.registration_expiry}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Inspection Expiry"
                    name="inspection_expiry"
                    type="date"
                    value={formData.inspection_expiry}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                {/* Service Info */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Service Information
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Last Service Date"
                    name="last_service_date"
                    type="date"
                    value={formData.last_service_date}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Last Service Mileage"
                    name="last_service_mileage"
                    type="number"
                    value={formData.last_service_mileage}
                    onChange={handleChange}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">km</InputAdornment>,
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Current Mileage"
                    name="current_mileage"
                    type="number"
                    value={formData.current_mileage}
                    onChange={handleChange}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">km</InputAdornment>,
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Next Service Mileage"
                    name="next_service_mileage"
                    type="number"
                    value={formData.next_service_mileage}
                    onChange={handleChange}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">km</InputAdornment>,
                    }}
                  />
                </Grid>

                {/* Purchase Info */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Purchase Information
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Purchase Date"
                    name="purchase_date"
                    type="date"
                    value={formData.purchase_date}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Purchase Cost"
                    name="purchase_cost"
                    type="number"
                    value={formData.purchase_cost}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">UGX</InputAdornment>,
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>Supplier</InputLabel>
                    <Select
                      name="supplier"
                      value={formData.supplier}
                      onChange={handleChange}
                      label="Supplier"
                    >
                      <MenuItem value="">None</MenuItem>
                      {suppliers.map((sup) => (
                        <MenuItem key={sup.id} value={sup.id}>
                          {sup.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Images Section */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Vehicle Images
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      border: '2px dashed',
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: 2,
                      textAlign: 'center',
                      position: 'relative',
                      minHeight: 150,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {imagePreview ? (
                      <>
                        <Box
                          component="img"
                          src={imagePreview}
                          sx={{
                            maxWidth: '100%',
                            maxHeight: 120,
                            objectFit: 'contain',
                            borderRadius: 1,
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveImage(1)}
                          sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'background.paper' }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <CloudUpload sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          Primary Image
                        </Typography>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageChange(e, 1)}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        cursor: 'pointer',
                      }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      border: '2px dashed',
                      borderColor: 'divider',
                      borderRadius: 2,
                      p: 2,
                      textAlign: 'center',
                      position: 'relative',
                      minHeight: 150,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {imagePreview2 ? (
                      <>
                        <Box
                          component="img"
                          src={imagePreview2}
                          sx={{
                            maxWidth: '100%',
                            maxHeight: 120,
                            objectFit: 'contain',
                            borderRadius: 1,
                          }}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveImage(2)}
                          sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'background.paper' }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <CloudUpload sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          Secondary Image
                        </Typography>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageChange(e, 2)}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        cursor: 'pointer',
                      }}
                    />
                  </Box>
                </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setFormOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={formLoading}>
            {isEditing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Vehicle Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Vehicle Details
          {selectedVehicle && (
            <Box component="span" sx={{ ml: 2 }}>
              <Chip
                label={selectedVehicle.status?.replace('_', ' ').toUpperCase()}
                color={STATUS_COLORS[selectedVehicle.status] || 'default'}
                size="small"
              />
              {!selectedVehicle.is_active && (
                <Chip
                  label="INACTIVE"
                  color="error"
                  size="small"
                  variant="outlined"
                  sx={{ ml: 1 }}
                />
              )}
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedVehicle && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Avatar
                      src={selectedVehicle.image}
                      variant="rounded"
                      sx={{ width: 100, height: 80 }}
                    >
                      <DirectionsCar sx={{ fontSize: 40 }} />
                    </Avatar>
                    {selectedVehicle.image_2 && (
                      <Avatar
                        src={selectedVehicle.image_2}
                        variant="rounded"
                        sx={{ width: 100, height: 80 }}
                      >
                        <DirectionsCar sx={{ fontSize: 40 }} />
                      </Avatar>
                    )}
                  </Box>
                  <Box>
                    <Typography variant="h6">{selectedVehicle.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedVehicle.vehicle_number} | {selectedVehicle.registration_number}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedVehicle.make} {selectedVehicle.model} ({selectedVehicle.year})
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary">Type</Typography>
                <Typography>{selectedVehicle.vehicle_type?.replace('_', ' ')}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary">Color</Typography>
                <Typography>{selectedVehicle.color || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary">Fuel Type</Typography>
                <Typography>{selectedVehicle.fuel_type}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary">Current Mileage</Typography>
                <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Speed fontSize="small" />
                  {selectedVehicle.current_mileage?.toLocaleString() || 0} km
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">Section/Unit</Typography>
                <Typography>{selectedVehicle.department_name || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">Assigned Driver</Typography>
                <Typography>{selectedVehicle.assigned_driver_name || 'Unassigned'}</Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">VIN</Typography>
                <Typography>{selectedVehicle.vin || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">Engine Number</Typography>
                <Typography>{selectedVehicle.engine_number || 'N/A'}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Insurance & Registration</Typography>
              </Grid>

              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">Insurance Company</Typography>
                <Typography>{selectedVehicle.insurance_company || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">Policy Number</Typography>
                <Typography>{selectedVehicle.insurance_policy_number || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">Insurance Expiry</Typography>
                <Typography color={isExpiringOrExpired(selectedVehicle.insurance_expiry) ? 'error' : 'inherit'}>
                  {selectedVehicle.insurance_expiry ? formatDate(selectedVehicle.insurance_expiry) : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">Registration Expiry</Typography>
                <Typography color={isExpiringOrExpired(selectedVehicle.registration_expiry) ? 'error' : 'inherit'}>
                  {selectedVehicle.registration_expiry ? formatDate(selectedVehicle.registration_expiry) : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6} md={4}>
                <Typography variant="caption" color="text.secondary">Inspection Expiry</Typography>
                <Typography color={isExpiringOrExpired(selectedVehicle.inspection_expiry) ? 'error' : 'inherit'}>
                  {selectedVehicle.inspection_expiry ? formatDate(selectedVehicle.inspection_expiry) : 'N/A'}
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Service Information</Typography>
              </Grid>

              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary">Last Service Date</Typography>
                <Typography>
                  {selectedVehicle.last_service_date ? formatDate(selectedVehicle.last_service_date) : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary">Last Service Mileage</Typography>
                <Typography>
                  {selectedVehicle.last_service_mileage ? `${Number(selectedVehicle.last_service_mileage).toLocaleString()} km` : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="text.secondary">Next Service Mileage</Typography>
                <Typography>
                  {selectedVehicle.next_service_mileage ? `${Number(selectedVehicle.next_service_mileage).toLocaleString()} km` : 'N/A'}
                </Typography>
              </Grid>

              {(selectedVehicle.purchase_date || selectedVehicle.purchase_cost || selectedVehicle.supplier_name) && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Purchase Information</Typography>
                  </Grid>

                  <Grid item xs={6} md={4}>
                    <Typography variant="caption" color="text.secondary">Purchase Date</Typography>
                    <Typography>
                      {selectedVehicle.purchase_date ? formatDate(selectedVehicle.purchase_date) : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <Typography variant="caption" color="text.secondary">Purchase Cost</Typography>
                    <Typography>
                      {selectedVehicle.purchase_cost ? `UGX ${Number(selectedVehicle.purchase_cost).toLocaleString()}` : 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={4}>
                    <Typography variant="caption" color="text.secondary">Supplier</Typography>
                    <Typography>{selectedVehicle.supplier_name || 'N/A'}</Typography>
                  </Grid>
                </>
              )}

              {selectedVehicle.notes && (
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary">Notes</Typography>
                  <Typography>{selectedVehicle.notes}</Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDetailOpen(false)}>
            Close
          </Button>
          {selectedVehicle?.status === 'operational' && selectedVehicle?.is_active && hasPermission('vehicle_requests.request') && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<DirectionsCarFilled />}
              onClick={() => handleOpenRequestModal(selectedVehicle)}
            >
              Request This Vehicle
            </Button>
          )}
          <PermissionGate permission="vehicles.edit">
            <Button onClick={handleEdit}>
              Edit
            </Button>
          </PermissionGate>
        </DialogActions>
      </Dialog>

      {/* Vehicle Request Modal */}
      <Dialog open={requestModalOpen} onClose={() => setRequestModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Request Vehicle
          {selectedVehicle && (
            <Typography variant="body2" color="text.secondary">
              {selectedVehicle.registration_number} - {selectedVehicle.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Destination"
                name="destination"
                value={requestFormData.destination}
                onChange={handleRequestChange}
                error={Boolean(requestErrors.destination)}
                helperText={requestErrors.destination}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Departure Date & Time"
                name="departure_date"
                type="datetime-local"
                value={requestFormData.departure_date}
                onChange={handleRequestChange}
                error={Boolean(requestErrors.departure_date)}
                helperText={requestErrors.departure_date}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Expected Return Date & Time"
                name="expected_return_date"
                type="datetime-local"
                value={requestFormData.expected_return_date}
                onChange={handleRequestChange}
                error={Boolean(requestErrors.expected_return_date)}
                helperText={requestErrors.expected_return_date}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Purpose"
                name="purpose"
                value={requestFormData.purpose}
                onChange={handleRequestChange}
                error={Boolean(requestErrors.purpose)}
                helperText={requestErrors.purpose}
                multiline
                rows={3}
                required
              />
            </Grid>

            {/* Passenger Information Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary">
                Passenger Information
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Staff on Board"
                name="num_staff_on_board"
                type="number"
                value={requestFormData.num_staff_on_board}
                onChange={handleRequestChange}
                inputProps={{ min: 1 }}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Non-Staff on Board"
                name="num_non_staff_on_board"
                type="number"
                value={requestFormData.num_non_staff_on_board}
                onChange={handleRequestChange}
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Your Rank/Position"
                name="requester_rank"
                value={requestFormData.requester_rank}
                onChange={handleRequestChange}
                placeholder="e.g., Senior Medical Officer"
                helperText="Your official rank or position for authorization"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setRequestModalOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleRequestSubmit} loading={requestFormLoading}>
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default VehiclesList
